import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  AdminGridCallbackEvent,
  AdminGridConfig,
  AdminGridDirective,
} from '../../../admin/shared/admin-grid.directive';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { QarStdDescEditComponent } from './qar-std-desc-edit/qar-std-desc-edit.component';

@Component({
  selector: 'gtw-qar-std-desc',
  templateUrl: './qar-std-desc.component.html',
  styleUrls: ['./qar-std-desc.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class QarStdDescComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();
  @ViewChild(AdminGridDirective) adminGridRef?: AdminGridDirective;

  private readonly subs = new Subscription();

  baseURLs: any = {};
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  userSettings: any = {};
  serverConfig: any = {};

  canCreateQARStdDesc = true;
  stdDescList: any[] = [];
  loading = false;
  private isInitialized = false;

  gridConfig: AdminGridConfig = {
    actionId: 32190,
    gridId: '5776',
    autoLoad: true,
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.userSettings = header.userSettingsData || {};
    this.serverConfig = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    this.isInitialized = true;
    const clientConfig = this.serverConfig?.clientConfig
      || this.GENERAL_CONFIG?.server_config_data?.clientConfig
      || {};
    const allowedUsers = clientConfig?.can_create_qar_std_desc || [];
    const currentUser = this.userSettings?.user?.sso_id || '';
    this.canCreateQARStdDesc = Array.isArray(allowedUsers) && allowedUsers.indexOf(currentUser) !== -1;
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.adminGridRef?.clearCache();
    this.adminGridRef?.refresh();
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    if (event.callName === 'stdDescEdit') {
      const rowData = event.data?.rowData ?? event.data;
      this.editStdDesc(rowData);
    }
  }

  onGridDataLoaded(data: any): void {
    this.stdDescList = Array.isArray(data?.data) ? data.data : [];
    this.cdr.detectChanges();
  }

  addStdDesc(): void {
    this.openEditor(null);
  }

  editStdDesc(row: any): void {
    this.openEditor(row);
  }

  private openEditor(rowData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '600px';
    config.maxWidth = '90vw';
    config.disableClose = true;
    config.data = {
      rowData: rowData || null,
      stdDescList: this.stdDescList,
      generalConfig: this.GENERAL_CONFIG,
    };

    const ref = this.dialog.open(QarStdDescEditComponent, config);
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result === 'saved') {
        this.requestGridUpdate.emit('refresh');
        this.adminGridRef?.clearCache();
        this.adminGridRef?.refresh();
      }
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
